import { useInvalidate } from '@refinedev/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { ordersCreateCancellationRequestBatchApprovalMutation } from '@repo/sdk'

import { useInvalidateFulfillmentOrders } from '../orders/useInvalidateFulfillmentOrders'

export function useApproveCancellationBatch() {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()
  const invalidateFulfillmentOrders = useInvalidateFulfillmentOrders()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutateAsync, isPending } = useMutation({
    ...ordersCreateCancellationRequestBatchApprovalMutation(),
    onSuccess: async (data) => {
      if (data.failedCount === 0) {
        message.success(t('common.modals.batchApproveSuccess', { count: data.processedCount }))
      } else {
        message.warning(
          t('common.modals.batchApprovePartial', {
            processed: data.processedCount,
            failed: data.failedCount,
          }),
        )
      }
      await Promise.all([
        // A batch touches many cancellations, so widen to the whole resource:
        // `detail` would need one id and would silently no-op without it.
        invalidate({ resource: 'cancellation-list', invalidates: ['resourceAll'] }),
        queryClient.invalidateQueries({ queryKey: ['cancellationTabCounts'] }),
        invalidateFulfillmentOrders(),
      ])
    },
    onError: () => {
      message.error(t('common.modals.batchApproveFailed'))
    },
  })

  /**
   * Resolves once the server answered (its outcome is already shown as a message). Rejections are
   * already reported by onError, so they are swallowed here and reported as `false`.
   */
  const approveBatch = async (cancellationIds: string[]): Promise<boolean> => {
    try {
      await mutateAsync({ body: { cancellationIds } })
      return true
    } catch {
      return false
    }
  }

  return { approveBatch, isApproving: isPending }
}
